import { useCallback, useEffect, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import API from '@enums/api'
import { PodiumEvent, PodiumFleetPagination } from '@types'
import useStoredPreference from '@hooks/useStoredPreference'
import CardPagination from './CardPagination'
import CopyableId from './CopyableId'
import { isPerPageOption } from './pageSizes'

interface Props {
    hardwareId: string
}

type TypeFilter = 'all' | PodiumEvent['type']

const DEFAULT_PER_PAGE = 25

/** Remembered across page loads, like the jobs card's. */
const FILTER_KEY = 'podium.eventsFilter'
const PER_PAGE_KEY = 'podium.eventsPerPage'

const isTypeFilter = (value: unknown): value is TypeFilter =>
    value === 'all' || value === 'error' || value === 'drift-reconciled'

/**
 * Per-device event history (the `PodiumEvents` log, 90-day retention): error events the
 * device published plus drift-reconciliation audit rows, newest first, with a Load-more
 * cursor through the server's pages. The "why did this station stop working" view.
 */
export default function PodiumEventsCard({ hardwareId }: Props) {
    const [events, setEvents] = useState<PodiumEvent[]>([])
    const [pagination, setPagination] = useState<PodiumFleetPagination | null>(
        null
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [typeFilter, setTypeFilter] = useStoredPreference<TypeFilter>(
        FILTER_KEY,
        'all',
        isTypeFilter
    )
    const [page, setPage] = useState(1)
    // Page NUMBER is deliberately not remembered — see the jobs card.
    const [perPage, setPerPage] = useStoredPreference<number>(
        PER_PAGE_KEY,
        DEFAULT_PER_PAGE,
        isPerPageOption
    )

    // Monotonic request id, checked after the await so only the latest fetch may commit
    // state: a slow page fetch trailing a filter switch would otherwise paint old-filter
    // rows over the new ones.
    const latestRequest = useRef(0)

    const fetchPage = useCallback(async () => {
        const requestId = latestRequest.current + 1
        latestRequest.current = requestId
        setLoading(true)
        setError(null)

        const res = await API.ha.podium
            .getEvents(hardwareId, {
                page,
                perPage,
                type: typeFilter === 'all' ? undefined : typeFilter,
            })
            .catch(() => null)
        if (requestId !== latestRequest.current) return
        setLoading(false)

        if (!res?.ok || !res.value?.pagination) {
            setError("Couldn't load the event history.")
            return
        }

        setEvents(res.value.data)
        setPagination(res.value.pagination)
    }, [hardwareId, page, perPage, typeFilter])

    useEffect(() => {
        void fetchPage()
    }, [fetchPage])

    return (
        <Paper sx={{ p: 3 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h6">
                    Event history
                    {pagination && pagination.total > 0 && (
                        <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                        >
                            {pagination.total}
                        </Typography>
                    )}
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={typeFilter}
                    onChange={(_e, v: TypeFilter | null) => {
                        if (!v) return
                        // A filter changes what page 1 even means, so never keep the
                        // current page number across a switch.
                        setTypeFilter(v)
                        setPage(1)
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="error">Errors</ToggleButton>
                    <ToggleButton value="drift-reconciled">Drift</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => void fetchPage()}
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {!error && !loading && events.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No events recorded in the last 90 days.
                </Typography>
            )}

            <Stack divider={<Divider flexItem />} spacing={1.5}>
                {events.map((event) => (
                    <Box key={event.id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                label={event.type}
                                color={
                                    event.type === 'error' ? 'error' : 'default'
                                }
                                size="small"
                                variant="outlined"
                            />
                            {event.stage && (
                                <Chip
                                    label={event.stage}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            <Typography variant="body2" color="text.secondary">
                                {dayjs(event.created_at).format(
                                    'YYYY/MM/DD HH:mm:ss'
                                )}
                            </Typography>
                        </Stack>
                        {/* Free text straight from the device payload: preserve its line
                            breaks and break long unbroken tokens (paths, URLs) so one
                            message can't widen the card. */}
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 0.5,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {event.message ?? '—'}
                        </Typography>
                        {(event.upload_id || event.session_id) && (
                            <Stack
                                direction="row"
                                spacing={1.5}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 0.25 }}
                            >
                                {event.upload_id && (
                                    <CopyableId
                                        label="upload"
                                        value={event.upload_id}
                                    />
                                )}
                                {event.session_id && (
                                    <CopyableId
                                        label="session"
                                        value={event.session_id}
                                    />
                                )}
                            </Stack>
                        )}
                    </Box>
                ))}
            </Stack>

            {loading && (
                <Stack direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <CircularProgress size={20} />
                </Stack>
            )}

            <CardPagination
                pagination={pagination}
                onChange={(nextPage, nextPerPage) => {
                    setPage(nextPage)
                    setPerPage(nextPerPage)
                }}
            />
        </Paper>
    )
}
